<script setup lang="ts">
import {useVuelidate} from '@vuelidate/core';
import {helpers, required, minLength} from "@vuelidate/validators";

const showWelcomeModal = ref(true);
const step = ref(1);
const eventsStore = useEventsStore();
const { eventContactDetails, eventData } = storeToRefs(eventsStore);

const apiErrors: Ref<any | null> = ref(null);
const isSubmitting = ref(false);

const wadError = useWadError();
const route = useRoute();
const eventId = Number(route.params.eventId);
const {needsLeadScanningWarning} = useWadAuth();
const showLeadScanningWarning = computed(() => needsLeadScanningWarning(eventId));
const {$api} = useNuxtApp();
const {getSession} = useAuth();

const rules = computed(() => {
  const rules = {
    first_name: {required: helpers.withMessage('Please enter your first name', required)},
    last_name: {required: helpers.withMessage('Please enter your last name', required)},
    job_position: {required: helpers.withMessage('Please enter your job position', required)},
    company: {required: helpers.withMessage('Please enter your company', required)},
    country: {required: helpers.withMessage('Please enter your country', required)},
  }
  if(step.value === 4) {
    rules.interests_reasons = {required, minLength: minLength(1),};
    rules.interests_topics =  {required, minLength: minLength(1),};
  }
  return rules;
});

// Bind to the ref (not eventContactDetails.value): if the store ref is reassigned mid-onboarding
// (e.g. a finished deployment re-fires the app.vue route watcher -> loadEventContactDetails), the
// template follows the new object while a captured snapshot would not. Passing the ref keeps
// validation tracking the same object the form edits, so the interests dropdowns no longer report
// "nothing selected" when selections are actually present.
const v$ = useVuelidate(rules, eventContactDetails as Ref<EventUserContactDetails>, {$lazy: true});

const errors = computed(() => ({
  first_name: v$.value.first_name.required.$invalid ? v$.value.first_name.required.$message : apiErrors.value?.fields?.first_name || null,
  last_name: v$.value.last_name.required.$invalid ? v$.value.last_name.required.$message : apiErrors.value?.fields?.last_name || null,
  job_position: v$.value.job_position.required.$invalid ? v$.value.job_position.required.$message : apiErrors.value?.fields?.job_position || null,
  company: v$.value.company.required.$invalid ? v$.value.company.required.$message : apiErrors.value?.fields?.company || null,
  country: v$.value.country.required.$invalid ? v$.value.country.required.$message : apiErrors.value?.fields?.country || null,
  interests_reasons: step.value === 4 ? (v$.value.interests_reasons.$invalid ? 'Please choose at least one option' : apiErrors.value?.fields?.interests_reasons || null) : null,
  interests_topics: step.value === 4 ? (v$.value.interests_topics.$invalid ? 'Please choose at least one option' : apiErrors.value?.fields?.interests_topics || null) : null,
  generic: apiErrors.value?.message ?? null
}));

const modalTitle = computed(() => {
  if(step.value === 1) {
    return eventData.value?.name ? `Welcome to ${eventData.value.name}!` : 'Welcome!'
  }
  if(step.value === 2) return 'Public contact information'
  if(step.value === 3) return 'Networking information'
  if(step.value === 4) return 'Event experience'
  if(step.value === 5) return 'Partner Data Sharing'
  return ''
})

const scrollToElement = async (elClass: string) => {
  const el = document.getElementsByClassName(elClass);
  await nextTick(() => {
    if(el && el[0]) {
      el[0].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  })
}

const updateContactDetailsForm = async () => {
  if (isSubmitting.value) return;

  v$.value.$touch();
  apiErrors.value = null;

  if(v$.value.$invalid) {
    await scrollToElement('wad4-input-group--invalid');
    return;
  }

  isSubmitting.value = true;
  try {
    if(step.value === 5 && eventContactDetails.value) {
      eventContactDetails.value.has_visited_homepage = true;
    }
    const {interests_options, interests_reasons, interests_topics, ...formData} = eventContactDetails.value as EventUserContactDetails;
    await $api.v2.updateContactDetails(eventId, Object.assign({
      interests_reasons: interests_reasons.map(interest => interest.id),
      interests_topics: interests_topics.map(interest => interest.id),
      ...(step.value === 5 ? { partner_data_acknowledged: true } : {}),
    }, formData));
    if (step.value < 5) {
      step.value++;
    } else {
      await getSession();
      showWelcomeModal.value = false;
    }
  } catch (e) {
    apiErrors.value = wadError.parseErrorFromException(e);
  } finally {
    isSubmitting.value = false;
    setTimeout(async () => {
      await scrollToElement('wad4-modal__header');
    }, 300);
  }
};

</script>

<template>
  <CommonWadModal class="wad4-onboarding-modal" :class="`wad4-onboarding-modal--step-${step}`" v-model="showWelcomeModal" @close="showWelcomeModal = false" :title="modalTitle" controlsClass="wad4-onboarding-modal__controls" :click-to-close="false">
    <template #content>
      <template v-if="step === 1">
        <div>
          <p class="wad4-onboarding-modal__modal-text">How great to see you here! </p>
          <p class="wad4-onboarding-modal__modal-text">Please confirm some basic details about yourself, so we can tailor a bit your experience: </p>
        </div>
        <div class="wad4-typo__text--semi-bold wad4-typo__text--lg">Public contact information</div>
        <form class="wad4-onboarding-modal__form" novalidate>
          <FormInputField label="First Name" v-model="eventContactDetails.first_name" type="text" :required="true" :hint="errors.first_name" :invalid="!!errors.first_name"/>
          <FormInputField label="Last Name" v-model="eventContactDetails.last_name" type="text" :required="true" :hint="errors.last_name" :invalid="!!errors.last_name"/>
          <FormInputField label="Job position" v-model="eventContactDetails.job_position" type="text" :required="true" :hint="errors.job_position" :invalid="!!errors.job_position"/>
          <FormInputField label="Company" v-model="eventContactDetails.company" type="text" :required="true" :hint="errors.company" :invalid="!!errors.company"/>
          <FormInputField label="Country" v-model="eventContactDetails.country" type="text" :required="true" :hint="errors.country" :invalid="!!errors.country"/>
          <FormGenricError v-if="errors.generic" :message="errors.generic"/>
          <div></div>
        </form>
      </template>
      <template v-if="step === 2">
        <form class="wad4-onboarding-modal__form" novalidate>
          <FormInputField label="About you" v-model="eventContactDetails.about" type="textarea" :required="false"/>
          <div class="wad4-my-badge__form-text wad4-typo__text--semi-bold">Social Media</div>
          <FormInputField label="LinkedIn" v-model="eventContactDetails.linkedin" type="text" :required="false"/>
          <FormInputField label="GitHub" v-model="eventContactDetails.github" type="text" :required="false"/>
          <FormInputField label="X (Twitter)" v-model="eventContactDetails.twitter" type="text" :required="false"/>
          <FormInputField label="Bluesky" v-model="eventContactDetails.bluesky" type="text" :required="false"/>
          <FormInputField label="Mastodon" v-model="eventContactDetails.mastodon" type="text" :required="false"/>
          <FormInputField label="Facebook" v-model="eventContactDetails.facebook" type="text" :required="false"/>
          <FormInputField label="Website/Blog/Other" v-model="eventContactDetails.website" type="text" :required="false"/>
          <div class="wad4-my-badge__attendee-list-checkbox">
            <label class="wad4-onboarding-modal__checkbox">
              <input id="single-checkbox" class="wad4-onboarding-modal__checkbox__input" type="checkbox" :checked="Boolean(eventContactDetails.hide_from_attendees)" @change="eventContactDetails.hide_from_attendees = $event.target.checked"/>
              <span>
                <svg v-if="eventContactDetails.hide_from_attendees === true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                  <rect x="3.5" y="3.5" width="17" height="17" rx="1.5" stroke="var(--wad4-c-text-text-link)" fill="var(--wad4-c-text-text-link)"/>
                  <path d="M7 12l3 3 7-7" stroke="white" stroke-width="2"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                  <rect x="3.5" y="3.5" width="17" height="17" rx="1.5" stroke="var(--wad4-c-text-text-link)"/>
                </svg>
              </span>
              <span class="wad4-onboarding-modal__checkbox-label">Hide my profile from the Attendees list.</span>
            </label>
          </div>
        </form>
      </template>
      <template v-if="step === 3">
        <p class="wad4-onboarding-modal__modal-text">Decide on the contact information that will be shared with attendees or companies when you connect with them through the app or badge scanning:</p>
        <form class="wad4-onboarding-modal__form" novalidate>
          <FormInputField label="Contact e-mail" :model-value="eventContactDetails.email" type="email" :disabled="true" :required="true"/>
          <FormInputField label="Phone number (optional)" v-model="eventContactDetails.phone_number" type="text" :required="false"/>
        </form>
      </template>
      <template v-if="step === 4">
        <p class="wad4-onboarding-modal__modal-text">Share your interests, so we can tailor your experience throughout the congress:</p>
        <div class="wad4-onboarding-modal__form">
          <FormSelectChipsField label="What are you here for?" :options="eventContactDetails.interests_options.reasons" v-model="eventContactDetails.interests_reasons" placeholder="Select one or more..." :hint="errors.interests_reasons" :invalid="!!errors.interests_reasons"/>
          <FormSelectChipsField label="What topics interest you?" :options="eventContactDetails.interests_options.topics" v-model="eventContactDetails.interests_topics" placeholder="Select one or more..." :hint="errors.interests_topics" :invalid="!!errors.interests_topics" select-placement="top-start"/>
        </div>
      </template>
      <template v-if="step === 5">
        <div class="wad4-onboarding-modal__form">
          <p class="wad4-onboarding-modal__modal-text">We share your contact details with a partner <u>only</u> when you choose to interact with that specific partner, such as by allowing them to scan your badge/QR code, registering for a partner-hosted session, workshop, or activity, or connecting with them in the app.</p>
          <p class="wad4-onboarding-modal__modal-text">We share these details so the partner can manage the activity and follow up with business or marketing communications about their products and services related to your interaction. <u>Partners you do not interact with do not receive your contact details.</u> You can find more on how your data is used, and your rights, in our <a href="https://www.wearedevelopers.com/about/legal/privacy-policy" target="_blank" class="wad4-text-link">Privacy Policy</a>.</p>
          <div v-if="showLeadScanningWarning" class="wad4-onboarding-modal__lead-scan-warning">
            <strong>Lead scanning not enabled!</strong>
            <p>Please tell your manager to add you as a related person in the WAD portal, in order to be able to scan leads at the event, using this app.</p>
          </div>
        </div>
      </template>
    </template>
    <template #controls>
      <div class="wad4-onboarding-modal__buttons">
        <button v-if="step > 1" @click="step = step - 1" type="button" class="wad4-button-secondary-grey wad4-onboarding-modal__button">Back</button>
        <button @click="updateContactDetailsForm" :disabled="isSubmitting" class="wad4-button-primary wad4-onboarding-modal__button" :class="{'wad4-button--width-full': step === 1}">
          {{ step === 5 ? 'I understand' : 'Continue' }}
        </button>
      </div>
      <CommonWadStepIndicator :total-steps="5" :current-step="step-1"/>
    </template>
  </CommonWadModal>
</template>

<style lang="scss">
.wad4-onboarding-modal {

  & .wad4-modal {
    @include mqBreakPoint($wad4-breakpoint-desktop-min) {
      position: relative;
    }
  }

  & .wad4-modal__content {
    gap: $wad4-container-spacing;
  }

  // The title is the step heading, in the display face of the redesigned pages.
  & .wad4-modal__title {
    @include title-display-mixin('xxs');
  }

  & .wad4-text-link {
    color: $wad4-c-text-text-link;
    text-decoration: underline;
  }

  &__modal-text {
    margin: unset;
    color: $wad4-c-text-text-primary;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-lg;
    align-self: stretch;
    align-items: stretch;
    padding-bottom: 80px;
    @include mqBreakPoint($wad4-breakpoint-tablet-min, $wad4-breakpoint-tablet-max) {
      padding-bottom: 90px;
    }
    @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
      padding-bottom: 98px;
    }
  }

  &__controls {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-lg;
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: $wad4-c-background-bg-primary;
    margin: 0 auto;
    padding-top: 15px;
    padding-bottom: 15px;
  }

  &__buttons {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    max-width: 380px;
    align-self: center;
    width: 100%;
    @include mqBreakPoint(0, $wad4-breakpoint-tablet-max) {
      max-width: 465px;
    }
    @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
      padding-left: $wad4-container-spacing;
      padding-right: $wad4-container-spacing;
    }
  }

  &__button {
    display: flex;
    padding: var(--spacing-md, 8px) var(--spacing-2xl, 20px);
    justify-content: center;
    align-items: center;
    gap: var(--spacing-xs, 4px);
    flex: 1 0 0;
  }

  &__lead-scan-warning {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-xs;
    padding: $wad4-spacing-md;
    border-radius: $wad4-border-radius-xs;
    background: $wad4-c-background-bg-warning-primary;
    color: $wad4-c-text-text-warning;

    strong {
      font-weight: $wad4-font-weight-semi-bold;
    }

    p {
      margin: unset;
    }
  }

  &__checkbox {
    display: flex;
    align-items: flex-start;
    gap: $wad4-spacing-xs;
    align-self: stretch;
    padding-top: $wad4-spacing-sm;
    padding-bottom: $wad4-spacing-md;

    &-label {
      color: $wad4-c-text-text-primary;
    }

    &__input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  &--step-1,
  &--step-2 {
    .wad4-onboarding-modal__form {
      @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
        padding-bottom: 80px;
      }
    }
  }
}
</style>
